/**
 * RoyalEngagement — content model + resolver.
 *
 * Data-driven template for North Indian royal engagement ceremonies.
 */

import type { InvitationData } from '../types';

function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1519741497674-611481863552', 2000),
  rings: img('1596394512593-4311d835b8e2', 900),
  couple: img('1519225421980-715cb0215aed', 900),
  family: img('1529636798458-92182e662485', 900),
  venue: img('1464366400600-7168b8af9bc3', 900),
  events: [
    img('1583939003579-730e3918a45a', 900),
    img('1519741497674-611481863552', 900),
  ],
};

// ── Types ─────────────────────────────────────────────────────────────────

export interface FamilyGroup {
  side: 'groom' | 'bride';
  title: string;
  members: { name: string; relation: string }[];
}

export interface Event {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  image?: string;
}

export interface RoyalContent {
  announcement: string;
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  families: {
    label: string;
    title: string;
    groups: FamilyGroup[];
  };
  ceremony: {
    label: string;
    title: string;
    description: string;
    image: string;
    date: string;
    time: string;
    venue: string;
  };
  programme: {
    label: string;
    title: string;
    events: Event[];
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Demo Content ───────────────────────────────────────────────────────────

const DEMO: RoyalContent = {
  announcement: '✨ Two families unite in love — you are cordially invited',
  hero: {
    kicker: 'Together with our families',
    title: 'Vikram & Priya',
    subtitle: 'are getting engaged',
    host: 'The Rathore & Malhotra Families',
    dateLabel: 'Saturday, 15 November 2025',
    venueShort: 'The Raj Palace, Jaipur',
    background: STOCK.hero,
  },
  families: {
    label: 'The Families',
    title: 'Blessed by elders, united by love',
    groups: [
      {
        side: 'groom',
        title: 'Rathore Family',
        members: [
          { name: 'Col. Arjun Rathore', relation: 'Father of the Groom' },
          { name: 'Mrs. Kamini Rathore', relation: 'Mother of the Groom' },
          { name: 'Capt. Arjun Rathore Jr.', relation: 'Brother of the Groom' },
        ],
      },
      {
        side: 'bride',
        title: 'Malhotra Family',
        members: [
          { name: 'Mr. Rajesh Malhotra', relation: 'Father of the Bride' },
          { name: 'Mrs. Sunita Malhotra', relation: 'Mother of the Bride' },
          { name: 'Rahul Malhotra', relation: 'Brother of the Bride' },
        ],
      },
    ],
  },
  ceremony: {
    label: 'The Ceremony',
    title: 'Ring Exchange',
    description:
      'Join us for the sacred ring ceremony where Vikram and Priya will exchange rings in the presence of family and friends, marking the beginning of their beautiful journey together.',
    image: STOCK.rings,
    date: 'Saturday, 15 November 2025',
    time: '7:00 PM onwards',
    venue: 'Royal Gardens, The Raj Palace, Jaipur',
  },
  programme: {
    label: 'Celebrations',
    title: 'A royal evening awaits',
    events: [
      {
        title: 'Tilak Ceremony',
        date: 'Friday, 14 November 2025',
        time: '11:00 AM',
        venue: 'Rathore Residence, Jaipur',
        description: 'Traditional blessing ceremony with family elders',
        image: STOCK.events[0],
      },
      {
        title: 'Engagement Ceremony',
        date: 'Saturday, 15 November 2025',
        time: '7:00 PM onwards',
        venue: 'Royal Gardens, The Raj Palace',
        description: 'Ring exchange followed by dinner and celebrations',
        image: STOCK.events[1],
      },
      {
        title: 'Reception Dinner',
        date: 'Saturday, 15 November 2025',
        time: '8:30 PM onwards',
        venue: 'Grand Ballroom, The Raj Palace',
        description: 'Royal feast with traditional Rajasthani cuisine',
        image: STOCK.events[0],
      },
    ],
  },
  rsvp: {
    label: 'Join Us',
    title: 'Kindly Respond',
    description:
      'Your presence will make this celebration even more special. Please let us know by the date below.',
    deadline: '1 November 2025',
  },
  footer: {
    message: 'With heartfelt gratitude and royal blessings',
    signature: 'Vikram & Priya',
  },
};

// ── Resolver ──────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): RoyalContent {
  const wedding = data.customData?.wedding ?? {};
  const [nameA, nameB] = (data.eventName || '').split(/\s*(?:&|and)\s*/i).filter(Boolean);
  const coupleNames = nameA && nameB ? `${nameA} & ${nameB}` : DEMO.hero.title;
  const groomName = nameA || 'Vikram';
  const brideName = nameB || 'Priya';

  const familyGroups: FamilyGroup[] | undefined = wedding.familyGroups?.length
    ? wedding.familyGroups.map((g: any) => ({
        side: g.side as 'groom' | 'bride',
        title: g.familyTitle || g.groupTitle,
        members: g.members
          .filter((m: any) => hasText(m.name) && hasText(m.relation))
          .map((m: any) => ({ name: m.name, relation: m.relation })),
      }))
    : undefined;

  const scheduleEvents: Event[] | undefined = wedding.weddingEvents?.length
    ? wedding.weddingEvents.map((e: any, i: number) => ({
        title: e.title,
        date: formatLong(e.date),
        time: [formatTime(e.startTime), formatTime(e.endTime)].filter(Boolean).join(' – '),
        venue: e.venue,
        description: e.description || '',
        image: e.coverImageUrl ?? STOCK.events[i % STOCK.events.length],
      }))
    : undefined;

  return {
    announcement: DEMO.announcement,
    hero: {
      kicker: DEMO.hero.kicker,
      title: coupleNames,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : DEMO.hero.host,
      dateLabel: hasText(data.eventDate) ? formatLong(data.eventDate) : DEMO.hero.dateLabel,
      venueShort: hasText(data.eventVenue) ? data.eventVenue : DEMO.hero.venueShort,
      background: data.mediaUrls?.[0] ?? DEMO.hero.background,
    },
    families: {
      ...DEMO.families,
      groups: familyGroups ?? DEMO.families.groups,
    },
    ceremony: {
      ...DEMO.ceremony,
    },
    programme: {
      ...DEMO.programme,
      events: scheduleEvents ?? DEMO.programme.events,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage) ? data.customMessage : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      message: DEMO.footer.message,
      signature: coupleNames,
    },
  };
}
